import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import alcoholUnitRelatedsFrom from 'components/units/UnitsRelatedFrom';
import { dynamicUnitName } from 'components/units/UnitDefinitionsAndMasks';
import { Button, Paper } from '@mui/material';
import { fillingStatusFromValue } from 'components/utils/fillingType';
import { shouldShowFillingActionInMainScreen } from 'components/utils/fillingActionPlacement';
import DoneIcon from '@mui/icons-material/Done';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import useMenu from 'menu-actions/useMenu';
import { getErrorMessage } from 'components/errorUtils';
import useGlobal from '../../../global-state/store';
import {
  NumberInput,
  LabelInputSelect,
} from '../BoxetteInputs';
import decimalsMask from '../DecimalsMask';
import { getDecimalPrecision } from '../getDecimalPrecision';
import validationSchemaLaying from './ValidationSchemaLaying';
import InventoryItem from '../InventoryItem';
import ChangeFillingDialog from '../ChangeFillingDialog';
import ChangeFillingToggle from '../ChangeFillingToggle';
import EditableField from '../EditableField';
import CustomOperatorFieldsInput from '../CustomOperatorFieldsInput';
import { useCarryForwardSummary } from '../components/CarryForwardIndicator';
import { validateFieldsAtSubmit } from './validateFieldsAtSubmit';
import { getDefaultFormValues } from './getDefaultFormValues';
import OperationNavigationBar from '../components/OperationNavigationBar';
import { FormValues, OtherContainerOperationProps } from './types';
import { getFullCapacityDisplay } from '../utils/capacityFallback';
import useVolumeOverCapacityConfirm from '../hooks/useVolumeOverCapacityConfirm';

export default function OtherContainerOperation({
  itemData,
  mappingConfig,
  recordOperation,
  nextOperation,
  previousItemFormValues,
  inventoryData,
  setInventoryData,
  canGoPrevious,
  canGoNext,
  onPreviousContainer,
  onNextContainer,
  containerSearchOptions,
  onSearchContainer,
  onSelectContainer,
}: OtherContainerOperationProps) {
  const { t } = useTranslation();
  const [globalState, globalActions] = useGlobal();
  const [calculating, setCalculating] = useState(false);
  const fillingStatus = fillingStatusFromValue(itemData[mappingConfig.columns.filling.mapping.csvName]);
  const containerType = 'volume';

  const isFullOrEmpty = ['full', 'empty'].includes(fillingStatus);
  const isFull = fillingStatus === 'full';
  const isEmpty = fillingStatus === 'empty';
  const { dispatchMenuActions } = useMenu();
  const [openChangeFilling, setOpenChangeFilling] = useState(false);
  const showChangeFillingInMainScreen = shouldShowFillingActionInMainScreen(mappingConfig);

  // Helper function to get display style for a field
  const getFieldDisplayStyle = (fieldName: string) => {
    const fieldConfig = mappingConfig.columns[fieldName];
    if (!fieldConfig?.enabled || !fieldConfig?.mapping?.csvName) {
      return 'hidden';
    }
    if (fieldConfig.displayStyle) {
      return fieldConfig.displayStyle;
    }
    // Container info fields default to editableText
    const containerInfoFields = ['totalCapacity', 'usableCapacity'];
    if (containerInfoFields.includes(fieldName)) {
      return 'editableText';
    }
    // All other fields default to inputField
    return 'inputField';
  };

  // Helper function to check if field should be hidden
  const isFieldHidden = (fieldName: string) => getFieldDisplayStyle(fieldName) === 'hidden';

  const {
    confirmVolume, dialog: volumeOverCapacityDialog, volumeInputRef,
  } = useVolumeOverCapacityConfirm({
    fillingStatus,
    liquidVolumeHidden: isFieldHidden('liquidVolume'),
  });

  const submitRecord = async (values: FormValues, proceedToNext: boolean) => {
    setCalculating(true);
    try {
      await recordOperation(values, proceedToNext);
      globalActions.setSnackbarMessage({
        message: `${t('success')}`,
        severity: 'success',
        duration: 1000,
        vertical: 'bottom',
      });
    } catch (err) {
      const errorMessage = getErrorMessage(err);

      globalActions.setSnackbarMessage({ message: `${t('unexpected_error')} : ${errorMessage}`, severity: 'warning' });
      // Keep the form values; the error has been reported to the operator.
    } finally {
      setCalculating(false);
    }
  };

  // Blocking checks first, then the operator's confirmation of a volume above capacity.
  const canRecord = async (values: FormValues) => await validateFieldsAtSubmit(
    values,
    mappingConfig.columns,
    fillingStatus,
    containerType,
    globalActions,
    t,
    mappingConfig.displayStyles,
  ) && confirmVolume(values);

  const submit = async (values: FormValues) => {
    if (await canRecord(values)) {
      await submitRecord(values, false);
    }
  };

  const handleSubmitAndNext = async () => {
    if (await canRecord(formik.values)) {
      await submitRecord(formik.values, true);
    }
  };

  const [alcoholUnitRelateds] = useState(
    alcoholUnitRelatedsFrom(
      globalState.userUnits.alcoholUnit,
      globalState.userUnits.temperatureUnit,
    ),
  );

  const initialValues: FormValues = getDefaultFormValues(
    t,
    itemData,
    mappingConfig.columns as any,
    globalState,
    previousItemFormValues,
    fillingStatusFromValue,
    mappingConfig.customFields as any,
  );

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema: validationSchemaLaying(t, alcoholUnitRelateds),
    onSubmit(values) {
      return submit(values);
    },
  });
  const carryForwardSummary = useCarryForwardSummary({
    formik,
    carriedValues: previousItemFormValues,
    // Stored values, as pickCarryForwardValues compares them
    storedValues: getDefaultFormValues(
      t,
      itemData,
      mappingConfig.columns as any,
      globalState,
      undefined,
      fillingStatusFromValue,
      mappingConfig.customFields as any,
    ),
    mappingConfig,
  });
  const fullCapacityDisplay = getFullCapacityDisplay(formik.values);

  const handleLiquidVolumeChange = (value: number) => {
    formik.setFieldValue('liquidVolume.value', value, false);
  };

  const reportChangeFillingAction = useCallback(() => ({
    name: t('operation.modify_filling'),
    callback: () => setOpenChangeFilling(true),
  }), [t]);

  useEffect(() => {
    const action = reportChangeFillingAction();
    dispatchMenuActions({ type: 'add', actions: [action] });
    return () => dispatchMenuActions({ type: 'remove', names: [action.name] });
  }, [dispatchMenuActions, reportChangeFillingAction]);

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'stretch',
        gap: 1,
        marginLeft: 'auto',
        marginRight: 'auto',
        maxWidth: 600,
      }}
    >
      <Paper elevation={0} sx={{ px: 3, py: 2 }}>
        <InventoryItem
          onClick={undefined}
          itemData={itemData}
          columnMappings={mappingConfig.columns}
          showFillingLabel
          containerABVStatus={itemData.abvStatus}
          containerVolumeStatus={itemData.volumeStatus}
        />
      </Paper>
      {showChangeFillingInMainScreen && (
        <ChangeFillingToggle
          inventoryData={inventoryData}
          itemData={itemData}
          setInventoryData={setInventoryData}
          formik={formik}
        />
      )}
      {!isFieldHidden('totalCapacity') && (
        <EditableField
          formik={formik}
          fieldKey="totalCapacity"
          translationKey="barrel_inventory.column_options.totalCapacity"
          dynamicUnitName={dynamicUnitName}
          decimalsMask={decimalsMask}
          displayStyle={getFieldDisplayStyle('totalCapacity')}
          mappingConfig={mappingConfig}
        />
      )}
      {!isFieldHidden('usableCapacity') && (
        <EditableField
          formik={formik}
          fieldKey="usableCapacity"
          translationKey="barrel_inventory.column_options.usableCapacity"
          dynamicUnitName={dynamicUnitName}
          decimalsMask={decimalsMask}
          displayStyle={getFieldDisplayStyle('usableCapacity')}
          mappingConfig={mappingConfig}
        />
      )}
      { isFullOrEmpty && (
      <Box sx={{
        display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1,
      }}
      >
        <Typography component="span">{`${t('ullaging.result_volume')} : `}</Typography>
        <Typography component="span">
          {isFull
            ? `${fullCapacityDisplay.value} ${dynamicUnitName(fullCapacityDisplay.unit)} `
            : `0 ${dynamicUnitName(fullCapacityDisplay.unit)} `}
          <span style={{ fontWeight: 'bold' }}>
            {t(`inventory_item.filling.${fillingStatus}`)}
          </span>
        </Typography>
      </Box>
      )}

      {!isEmpty && (
      <LoadingButton
        variant="contained"
        loading={calculating}
        disabled={!formik.isValid}
        onClick={handleSubmitAndNext}
        endIcon={<ArrowForwardIosIcon />}
      >
        {t('inventory_item.record_and_continue')}
      </LoadingButton>
      )}

      <Paper
        elevation={0}
        sx={{
          px: { xs: 1, md: 3 },
          py: 2,
          border: 1,
          borderColor: 'secondary.main',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 'bold',
            color: 'primary.main',
            position: 'absolute',
            top: 2,
            right: 10,
          }}
        >
          Volume
        </Typography>
        {carryForwardSummary([['volumeTemp', t('operation.carry_forward_indicators.field_temperature')]])}

        { !isFullOrEmpty && !isFieldHidden('liquidVolume') && (
          <LabelInputSelect
            label={
              <Typography component="span">{t('ullaging.liquid_volume')}</Typography>
                    }
            input={(
              <NumberInput
                id="liquidVolume.value"
                inputRef={volumeInputRef}
                disabled={isEmpty}
                name="liquidVolume.value"
                onValueChangeCallback={handleLiquidVolumeChange}
                placeholder="ex: 12.2"
                value={formik.values.liquidVolume.value}
                decimalScale={getDecimalPrecision('liquidVolume', formik.values.liquidVolume.unit, mappingConfig)}
                allowNegative
                onBlur={formik.handleBlur}
              />
            )}
            error={formik.touched.liquidVolume?.value && formik.errors.liquidVolume?.value && (
            <Typography component="span" style={{ fontSize: 13, color: 'red' }}>
              {formik.errors.liquidVolume?.value}
            </Typography>
            )}
            select={(
              <Typography component="span" sx={{ p: 2 }}>
                {dynamicUnitName(formik.values.liquidVolume.unit)}
              </Typography>
            )}
          />
        )}

        {!isFieldHidden('volumeTemperature') && (
          <LabelInputSelect
            label={(
              <Typography component="span">
                {t('forms.liquid_temp')}
              </Typography>
            )}
            input={(
              <NumberInput
                id="volumeTemp.value"
                disabled={isEmpty}
                name="volumeTemp.value"
                onValueChangeCallback={(value: number) => formik.setFieldValue('volumeTemp.value', value)}
                placeholder="ex: 12.2"
                value={formik.values.volumeTemp.value}
                decimalScale={getDecimalPrecision('volumeTemperature', formik.values.volumeTemp.unit, mappingConfig)}
                allowNegative
                onBlur={formik.handleBlur}
              />
            )}
            error={formik.touched.volumeTemp?.value && formik.errors.volumeTemp?.value && (
              <Typography component="span" style={{ fontSize: 13, color: 'red' }}>
                {formik.errors.volumeTemp?.value}
              </Typography>
            )}
            select={(
              <Typography component="span" sx={{ p: 2 }}>
                {dynamicUnitName(formik.values.volumeTemp.unit)}
              </Typography>
            )}
          />
        )}

      </Paper>

      <Paper
        elevation={0}
        sx={{
          px: { xs: 1, md: 3 },
          py: 2,
          border: 1,
          borderColor: 'ourOrange.main',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >

        <Typography
          variant="caption"
          sx={{
            fontWeight: 'bold',
            color: 'ourOrange.main',
            position: 'absolute',
            top: 2,
            right: 10,
          }}
        >
          {t('inventory_item.status_caption_label.abvStatus')}
        </Typography>
        {carryForwardSummary([
          ['tav', t('operation.carry_forward_indicators.field_tav')],
          ['tavTemp', t('operation.carry_forward_indicators.field_temperature')],
        ])}
        {!isFieldHidden('tav') && (
          <LabelInputSelect
            label={(
              <Typography component="span">
                {t('forms.measured_tav')}
              </Typography>
            )}
            input={(
              <NumberInput
                id="tav.value"
                disabled={isEmpty}
                onValueChangeCallback={(value: number) => formik.setFieldValue('tav.value', value)}
                name="tav.value"
                placeholder="ex: 56.88"
                value={formik.values.tav.value}
                decimalScale={getDecimalPrecision('tav', formik.values.tav.unit, mappingConfig)}
                onBlur={formik.handleBlur}
              />
            )}
            error={formik.touched.tav?.value && formik.errors.tav?.value && (
              <Typography component="span" style={{ fontSize: 13, color: 'red' }}>
                {formik.errors.tav?.value}
              </Typography>
            )}
            select={(
              <Typography component="span" sx={{ p: 2 }}>
                {dynamicUnitName(formik.values.tav.unit)}
              </Typography>
            )}
          />
        )}

        {!isFieldHidden('tavTemperature') && (
          <LabelInputSelect
            label={(
              <Typography component="span">
                {t('forms.measured_temp')}
              </Typography>
            )}
            input={(
              <NumberInput
                id="tavTemp.value"
                disabled={isEmpty}
                name="tavTemp.value"
                onValueChangeCallback={(value: number) => formik.setFieldValue('tavTemp.value', value)}
                placeholder="ex: 12.2"
                value={formik.values.tavTemp.value}
                decimalScale={getDecimalPrecision('tavTemperature', formik.values.tavTemp.unit, mappingConfig)}
                allowNegative
                onBlur={formik.handleBlur}
              />
            )}
            error={formik.touched.tavTemp?.value && formik.errors.tavTemp?.value && (
              <Typography component="span" style={{ fontSize: 13, color: 'red' }}>
                {formik.errors.tavTemp?.value}
              </Typography>
            )}
            select={(
              <Typography component="span" sx={{ p: 2 }}>
                {dynamicUnitName(formik.values.tavTemp.unit)}
              </Typography>
            )}
          />
        )}
      </Paper>

      <CustomOperatorFieldsInput
        mappingConfig={mappingConfig}
        formik={formik}
        itemData={itemData}
        carryForwardSummary={carryForwardSummary}
      />

      {isEmpty && (
        <Button
          onClick={handleSubmitAndNext}
          variant="contained"
          endIcon={<ArrowForwardIosIcon />}
        >
          {t('inventory_item.validate_and_continue')}
        </Button>
      )}
      {isEmpty && (
      <Button
        type="submit"
        variant="contained"
        endIcon={<DoneIcon />}
      >
        {t('validate')}
      </Button>
      )}

      {!isEmpty && (
        <LoadingButton
          variant="contained"
          loading={calculating}
          disabled={!formik.isValid}
          onClick={handleSubmitAndNext}
          endIcon={<ArrowForwardIosIcon />}
        >
          {t('inventory_item.record_and_continue')}
        </LoadingButton>
      )}
      {!isEmpty && (
        <LoadingButton
          variant="contained"
          type="submit"
          loading={calculating}
          disabled={!formik.isValid}
          endIcon={<DoneIcon />}
        >
          {t('validate')}
        </LoadingButton>
      )}

      <OperationNavigationBar
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
        onPrevious={onPreviousContainer}
        onNext={onNextContainer}
        options={containerSearchOptions}
        onSearchContainer={onSearchContainer}
        onSelectContainer={onSelectContainer}
      />
      {volumeOverCapacityDialog}
      <ChangeFillingDialog
        open={openChangeFilling}
        setOpen={setOpenChangeFilling}
        itemData={itemData}
        setInventoryData={setInventoryData}
        inventoryData={inventoryData}
        formik={formik}
      />
    </Box>
  );
}
